import { Button, Card, Chip, ModalBody, ModalContent, ModalFooter, ScrollShadow } from '@nextui-org/react'
import { FC } from 'react'
import useHomeRemoveGroupStore from '../../store/home/useHomeRemoveGroupStore'
import { BaseDivider, BaseModal, BaseModalHeader } from '../common/Modal'
import { useRemoveGroup } from '../../hooks/useRemoveGroup'

export interface SelectTanksToRemoveProps {
  isOpen: boolean
  onOpenChange: () => void
  isBack: () => void
}

export const SelectTanksToRemove: FC<SelectTanksToRemoveProps> = ({
  isOpen,
  onOpenChange,
  isBack,
}) => {
  const {
    selectedGroupId,
    selectedGroupName,
    tanksInGroup,
    selectedTanksToRemove,
    toggleTankSelection,
    setTanksBorderColor,
    selectTanksBorderColor,
    reset,
  } = useHomeRemoveGroupStore()

  const removeGroupMutation = useRemoveGroup()

  const handleBack = (onClose: () => void) => {
    onClose()
    setTimeout(() => {
      isBack()
    }, 50)
  }

  const handleRemove = async (onClose: () => void) => {
    setTanksBorderColor()

    if (selectedGroupId && selectedTanksToRemove.length > 0) {
      try {
        await removeGroupMutation.mutateAsync({
          groupId: selectedGroupId,
          schoolIds: selectedTanksToRemove.map((tank) => tank.schoolId),
        })
        onClose()
        reset()
      } catch (error) {
        console.error('Failed to remove tanks from group:', error)
      }
    }
  }

  const isSelected = (tankId: string) => {
    return selectedTanksToRemove.some((t) => t.tankId === tankId)
  }

  return (
    <BaseModal isOpen={isOpen} onOpenChange={onOpenChange} size={'3xl'} isDismissable={true}>
      <ModalContent className="overflow-x-hidden">
        {(onClose) => (
          <>
            <BaseModalHeader
              title="Select Tanks to Remove"
              description={`Remove tanks from "${selectedGroupName}"`}
            />
            <ModalBody className="gap-1 min-h-[8rem] pl-6 sm:pl-10 pr-6 sm:pr-10 overflow-x-hidden overflow-y-auto">
              <BaseDivider />

              {/* Tanks in group - clickable chips */}
              <div className="mt-3 font-bold">Tanks in Group</div>
              <Card
                className={`${selectTanksBorderColor} border-opacity-60 p-0`}
                radius="md"
                shadow="sm"
              >
                <ScrollShadow className="max-h-[200px]">
                  <div className="flex flex-wrap gap-2 p-3">
                    {tanksInGroup.length === 0 ? (
                      <div className="text-center text-gray-500 py-4 w-full">
                        No tanks in this group.
                      </div>
                    ) : (
                      tanksInGroup.map((tank) => (
                        <Chip
                          key={tank.tankId}
                          radius="md"
                          className={`cursor-pointer transition-all duration-200 ${isSelected(tank.tankId)
                            ? ''
                            : 'hover:bg-tdpldarkgrey hover:text-white'
                            }`}
                          classNames={{
                            base: `${isSelected(tank.tankId)
                              ? 'bg-tdplplum border-tdplhibiscus border-[2px]'
                              : 'bg-tdplsky border-tdpldarkgrey border-[1px]'
                              } border-opacity-60`,
                            content: 'font-bold',
                          }}
                          onClick={() => toggleTankSelection(tank)}
                        >
                          {tank.tankName}
                        </Chip>
                      ))
                    )}
                  </div>
                </ScrollShadow>
              </Card>

              <p className="text-xs text-tdpldarkgrey mt-1">
                Click on tanks to select them for removal
              </p>

              {selectTanksBorderColor.includes('tdplmaroon') && (
                <p className="m-0 text-sm font-semibold text-tdplmaroon">
                  Please select at least one tank to remove.
                </p>
              )}

              <BaseDivider />

              {/* Preview: Tanks to be removed */}
              <div className="mt-3 font-bold flex items-center gap-2">
                <span>Tanks to Remove</span>
              </div>
              {/* <div className="mt-3 font-bold">Selected Tanks</div> */}
              <Card
                className="border border-tdpldarkgrey border-opacity-60 p-0"
                radius="md"
                shadow="sm"
              >
                <div className="flex flex-wrap gap-2 p-3 min-h-[60px]">
                  {selectedTanksToRemove.length === 0 ? (
                    <div className="text-center text-gray-400 text-sm py-2 w-full">
                      No tanks selected for removal
                    </div>
                  ) : (
                    selectedTanksToRemove.map((tank) => (
                      <Chip
                        key={tank.tankId}
                        radius="md"
                        classNames={{
                          base: 'bg-tdplplum border-tdplhibiscus border-[2px] border-opacity-60',
                          content: 'font-bold',
                        }}
                      >
                        {tank.tankName}
                      </Chip>
                    ))
                  )}
                </div>
              </Card>
            </ModalBody>

            <ModalFooter className="pb-6 sm:pb-8 pl-6 sm:pl-10 pr-6 sm:pr-10">
              <div className="flex gap-3 justify-end w-full">
                <Button
                  variant="flat"
                  className="font-bold text-tdpldarkgrey bg-tdpllightgrey"
                  onPress={() => handleBack(onClose)}
                >
                  Back
                </Button>
                <Button
                  className="font-bold text-white w-[6rem] mr-0 bg-tdplplum"
                  onPress={() => handleRemove(onClose)}
                  isLoading={removeGroupMutation.isPending}
                  isDisabled={tanksInGroup.length === 0}
                >
                  {removeGroupMutation.isPending ? 'Removing...' : 'Remove'}
                </Button>
              </div>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </BaseModal>
  )
}
